/**
 * What each page is for: the explainers behind the "i" next to page titles
 * (PageHeading). One file, so the wording can be read and kept consistent in one
 * place as new staff arrive (Jesse, 2026-10-30).
 *
 * Write for the radio shop (frontend/CLAUDE.md "UI Copy"): what the page does and
 * what each control means for them, never how it works inside. Raise a page's
 * `version` when its text changes enough that everyone should see it again.
 */
import { Link } from 'react-router-dom'
import { HelpList, type PageHelp } from '../components/PageHeading'

const b = (text: string) => <span className="font-medium">{text}</span>
const heading = (text: string) => <p className="font-medium text-gray-900">{text}</p>

export const SEARCH_HELP: PageHelp = {
  id: 'search',
  version: 1,
  title: 'About License Search',
  content: (
    <>
      <p>
        Every FCC license we import, searchable. Use it to look a license up, or to build a list
        of prospects.
      </p>
      <HelpList>
        <li>
          The search box matches a call sign, licensee or contact name, city, phone, e-mail or FRN
          as you type.
        </li>
        <li>
          The page opens on licenses <span className="font-medium">expiring in the next 120
          days</span>, because renewals are the usual question. Set Expiration date to "Any"
          to search everything.
        </li>
        <li>
          {b('Construction notification due')} finds licenses with a build-out deadline coming
          up. Choosing it sets the expiration range to any, and the other way round.
        </li>
        <li>
          {b('Advanced Filters')} holds the rarer ones (name fields, frequency, location, Do Not
          Contact). Its toggle says how many are active, even while it is closed.
        </li>
      </HelpList>
      {heading('With the results')}
      <HelpList>
        <li>The icons on each row add the license as a lead or a client.</li>
        <li>
          {b('Save This Search')} keeps the filters. On a saved search, the bell e-mails you
          new matches, and {b('Leads')} puts its matches on the team's Auto-Discover list.
        </li>
        <li>{b('Export to CSV')} downloads the results; each export is recorded.</li>
      </HelpList>
    </>
  ),
}

export const LEADS_HELP: PageHelp = {
  id: 'leads',
  version: 1,
  title: 'About Leads',
  content: (
    <>
      <p>Licensees we might work for, before they become clients.</p>
      <HelpList>
        <li>
          {b('Tracked Leads')} are the ones somebody has picked up: each has a priority, an
          assignee, notes and a progress (Not started, Working, Waiting on them). Sending a message
          moves a lead that hasn't been started to Working.
        </li>
        <li>
          {b('Auto-Discover')} lists licenses expiring soon that nobody tracks yet, plus the
          matches of any saved search set to feed leads. Track one to move it to Tracked Leads.
        </li>
        <li>
          On a tracked lead, the briefcase converts it to a client, the circle dismisses it, and
          the bin deletes it with its notes.
        </li>
        <li>
          Leads on the Do Not Contact list never appear in Auto-Discover.
        </li>
      </HelpList>
    </>
  ),
}

export const CLIENTS_HELP: PageHelp = {
  id: 'clients',
  version: 1,
  title: 'About Clients',
  content: (
    <>
      <p>
        The licensees we work for, each with their licenses, contacts, tasks and communication
        history.
      </p>
      <HelpList>
        <li>
          {b('Status')} says whether we want them as a client; {b('Stale')} says there is
          nothing live: no license in force or recently lapsed, no contact logged, no task open
          and no mail queued.
        </li>
        <li>
          {b('Ownership change')} marks a client whose license changed hands or name at the
          FCC. Check it on Ownership Changes.
        </li>
        <li>
          Open a client to link licenses, log calls and e-mails, add tasks and send messages.
        </li>
      </HelpList>
    </>
  ),
}

export const TASKS_HELP: PageHelp = {
  id: 'tasks',
  version: 1,
  title: 'About Tasks',
  content: (
    <HelpList>
      <li>
        Follow-ups for the team, usually attached to a client or a lead. A task can send its
        assignee a reminder before it is due.
      </li>
      <li>
        The tabs narrow the list to yours, overdue ones, or ones nobody is assigned to.
        Completed tasks show under "All statuses".
      </li>
    </HelpList>
  ),
}

export const OWNERSHIP_HELP: PageHelp = {
  id: 'ownershipChanges',
  version: 1,
  title: 'About License Ownership Changes',
  content: (
    <HelpList>
      <li>
        When the FCC data we import changes the licensee name or FRN on an existing license, it
        lands here. It may be a sale or transfer, or just a company rename.
      </li>
      <li>
        Changes on our clients' licenses matter most: the client may have sold the system, or
        the new owner may need us.
      </li>
      <li>
        {b('Mark reviewed')} once checked; reviewed changes leave the default view. Select
        several to review them together.
      </li>
    </HelpList>
  ),
}

export const COORDINATION_HELP: PageHelp = {
  id: 'coordination',
  version: 1,
  title: 'About this frequency search',
  content: (
    <HelpList>
      <li>
        Lists the frequencies licensed for transmitters within a distance of a place, to help
        pick or check a frequency before filing.
      </li>
      <li>
        It is a starting point, not a coordination. Some licenses have no transmitter
        coordinates and cannot appear; the list under the form says which bands that affects.
      </li>
    </HelpList>
  ),
}

export const REFERENCE_COPY_HELP: PageHelp = {
  id: 'referenceCopy',
  version: 1,
  title: 'About the reference copy',
  content: (
    <HelpList>
      <li>
        Our reproduction of the FCC's reference copy of this license, built from the FCC's
        public records. It is handy for checking the license's details or giving a client a copy.
      </li>
      <li>
        The FCC didn't issue it, and someone who doesn't know us can take it for an official
        notice. For a first contact, use Send message with one of the reference copy e-mails
        instead.
      </li>
    </HelpList>
  ),
}

export const DNC_HELP: PageHelp = {
  id: 'doNotContact',
  version: 1,
  title: 'About Do Not Contact',
  content: (
    <HelpList>
      <li>
        People who asked us not to contact them, matched by call sign, licensee name or
        license. They are flagged in search results, kept out of Auto-Discover leads, and anyone
        about to send them a message is warned first.
      </li>
      <li>
        Anyone can add an entry from a search result and record why. Only an administrator can
        take somebody off the list, and every removal, and every message sent despite the
        warning, is flagged in the Audit Log.
      </li>
      <li>
        Tick "Show entries that have been removed" to see who was taken off, when and by whom.
      </li>
    </HelpList>
  ),
}

export const AUDIT_LOG_HELP: PageHelp = {
  id: 'auditLog',
  version: 2,
  title: 'About the Audit Log',
  content: (
    <HelpList>
      <li>
        A record of who did what and when: sign-ins, changes to clients, leads and licenses,
        messages, exports and admin changes. Entries can't be edited. Sign-in, security and admin
        entries are kept for a year; most others for 90 days or less.
      </li>
      <li>
        {b('Show')} picks the kinds of activity; untick "Sign-in & security" to look through
        everyday work.
      </li>
      <li>
        {b('Flagged for review only')} is the review queue: entries worth a second look that
        nobody has reviewed yet, which is what the menu badge counts. Tick them and press
        {' '}{b('Mark reviewed')}, with a note if useful. Who reviewed what is recorded.
      </li>
      <li>
        {b('Call sign or record')} finds every entry about one license, client or lead.
      </li>
    </HelpList>
  ),
}

export const SECURITY_ALERTS_HELP: PageHelp = {
  id: 'securityAlerts',
  version: 1,
  title: 'About Security Alerts',
  content: (
    <>
      <p>
        Things an administrator should look at now, picked out of the Audit Log as they
        happen.
      </p>
      <HelpList>
        <li>
          {b('Critical and high')} alerts e-mail every administrator and put a red banner on
          every page for admins: a passkey that may have been copied, a reused trusted-device
          key, a locked account, many failed sign-ins from one address, someone made an
          administrator, two-step sign-in relaxed for an account, a backup or the client data
          downloaded, a backup restored.
        </li>
        <li>
          {b('Medium')} alerts are only listed here: a message sent to someone on Do Not
          Contact, several people taken off Do Not Contact at once, and unusually large or
          frequent license exports.
        </li>
        <li>
          A repeat of the same thing adds to the open alert ("seen 12 times") instead of
          sending another e-mail. At most 12 alert e-mails go out in any hour.
        </li>
        <li>
          {b('Mark handled')} once you have looked, with a note on what you found. That clears
          the banner for everyone and is recorded in the Audit Log.
        </li>
      </HelpList>
    </>
  ),
}

// --- Construction --------------------------------------------------------------

/** What each Construction Deadlines row action does: the hover cards and the "i" share it. */
export const CONSTRUCTION_ACTION_HELP = {
  prepare: 'Opens our internal worksheet with everything needed to file the construction '
    + 'notification (FCC Form 601, Schedule K). It is for us: don\'t send it to the licensee.',
  lead: 'Starts tracking this licensee as a lead, with a note about the deadline. Use it for '
    + 'licensees we don\'t work with yet.',
  message: 'Writes to the licensee, starting from the courtesy notice e-mail about the deadline. '
    + 'Once it has gone, the row shows as contacted.',
  contact: 'Records a phone call, visit or letter about the deadline, so the row stops showing '
    + 'as needing a first contact.',
  ack: 'Marks the deadline as handled, for example after the notification is filed. It drops '
    + 'out of this list and the dashboard count. The FCC deadline itself does not change.',
  unack: 'Puts the deadline back in the list and the dashboard count.',
}

export const CONSTRUCTION_DEADLINES_HELP: PageHelp = {
  id: 'constructionDeadlines',
  version: 1,
  title: 'About Construction Deadlines',
  content: (
    <>
      <p>
        Licenses with an open FCC construction obligation: a build-out deadline with no
        construction recorded yet. The licensee must file a construction notification (FCC Form
        601) before the deadline, or the FCC can terminate the authorization for those
        frequencies.
      </p>
      {heading('Row actions')}
      <HelpList>
        <li>{b('Prepare 601')}: {CONSTRUCTION_ACTION_HELP.prepare}</li>
        <li>{b('Add as lead')}: {CONSTRUCTION_ACTION_HELP.lead}</li>
        <li>{b('Send message')}: {CONSTRUCTION_ACTION_HELP.message}</li>
        <li>{b('Log contact')}: {CONSTRUCTION_ACTION_HELP.contact}</li>
        <li>{b('Acknowledge')}: {CONSTRUCTION_ACTION_HELP.ack}</li>
      </HelpList>
      {heading('Finding more')}
      <HelpList>
        <li>
          Untick {b('Linked to a client/lead only')} to see licensees we don't work with yet.
        </li>
        <li>
          Rows that still need a first contact are highlighted, and the ones due within 60 days
          are counted on the menu. Contacted rows are greyed.
        </li>
        <li>
          To look for prospects in bulk, use{' '}
          <Link to="/search" className="text-primary-600 hover:underline">Search</Link> with
          {' '}{b('Construction notification due')}. That search can be saved, and can feed
          leads automatically.
        </li>
      </HelpList>
    </>
  ),
}

export const CONSTRUCTION_WORKSHEET_HELP: PageHelp = {
  id: 'constructionWorksheet',
  version: 1,
  title: 'About the Prepare 601 worksheet',
  content: (
    <HelpList>
      <li>
        {b("It's for our own staff.")} It gathers what you need to file the construction
        notification (FCC Form 601, Schedule K) in the FCC's licensing system.
      </li>
      <li>
        {b("Don't send it, or a printout of it, to the licensee.")} On its own it can look like
        an official FCC notice. The printed copy says "internal" at the top for the same reason.
      </li>
      <li>
        To let a licensee know their deadline is coming up, use{' '}
        <Link to="/construction-deadlines" className="text-primary-600 hover:underline">
          Construction Deadlines
        </Link>{' '}
        → Send message, which starts from the courtesy notice e-mail.
      </li>
      <li>
        Once the notification is filed, use {b('Mark 601 as filed')} below, with the ULS file
        number if you have it. The deadline then counts as handled.
      </li>
    </HelpList>
  ),
}
